<template>
  <div
    class="c-file-browser__item d-flex justify-content-center align-items-center w-100 content"
    :class="{ 'small-icon': smallIcon, 'h-100': !isFolderItem }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    v-html="itemSvg"
  />
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
import {drawFileSvg, playGifThumbnail, stopGifThumbnail} from '@frontend/utils';
import { FILE_TYPE } from '@frontend/constants/file-image-types';

const MISSING_IMAGE_URL = '/images/missing-image.png';

export default {
  name: "ItemView",
  inject: {
    smallIcon: {
      from: 'smallIcon',
      default: false
    }
  },
  props: {
    item: {
      type: Object,
      required: true,
    }
  },
  computed: {
    isFolderItem() {
      return this.item.type === FILE_TYPE.FOLDER;
    },
    itemSvg() {
      return drawFileSvg(this.item);
    }
  },
  mounted() {
    this.bindMissingImageFallback();
  },
  updated() {
    this.bindMissingImageFallback();
  },
  methods: {
    drawFileSvg: drawFileSvg,
    // Assigned in JS rather than an inline `onerror` attribute, which the CSP blocks.
    bindMissingImageFallback() {
      this.$el.querySelectorAll('img:not([data-fallback])').forEach((img) => {
        img.dataset.fallback = '';
        img.addEventListener('error', () => this.showMissingImage(img));

        // v-html starts the load during the patch, so it can already have failed by now.
        if (img.complete && img.naturalWidth === 0) {
          this.showMissingImage(img);
        }
      });
    },
    showMissingImage(img) {
      if (img.getAttribute('src') === MISSING_IMAGE_URL) {
        return;
      }

      img.src = MISSING_IMAGE_URL;
      this.$emit('image-missing');
    },
    onMouseEnter(event) {
      playGifThumbnail(event.currentTarget);
    },
    onMouseLeave(event) {
      stopGifThumbnail(event.currentTarget);
    }
  }
}
</script>

<style lang="scss">
.c-file-browser__item {
  img, svg, .folder-icon-preview {
    max-width: 170px;
    max-height: 110px;
  }

  // File-type glyphs (pdf/doc/etc). Do not apply to customized folder icons —
  // those need the Lovable 88×72 box so the badge stays centered on the folder face.
  > svg {
    width: 80px;
    height: 80px;
  }

  .folder-icon-preview {
    width: 88px;
    height: 72.16px;
    max-width: 88px;
    max-height: 72.16px;
    flex-shrink: 0;

    > svg {
      width: 100% !important;
      height: 100% !important;
      max-width: none !important;
      max-height: none !important;
    }

    .folder-icon-up-arrow {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-top: 12%;
      box-sizing: border-box;
      pointer-events: none;

      svg {
        width: 42% !important;
        height: 42% !important;
        max-width: none !important;
        max-height: none !important;
      }
    }
  }
}

.folder-items {
  .content {
    width: 100% !important;
    flex: 0 0 var(--c-item-folder-preview-height, 88px) !important;
    height: var(--c-item-folder-preview-height, 88px) !important;
    max-height: var(--c-item-folder-preview-height, 88px) !important;
    background-color: transparent;

    img {
      max-height: 80px;
    }

    &.small-icon {
      .folder-icon-preview {
        width: 28px;
        height: 22.96px;
        max-width: 28px;
        max-height: 22.96px;
      }

      .folder-icon-emoji {
        font-size: 11.76px !important;
        padding-top: 3.36px !important;
      }
    }
  }
}

/* Density mode overrides fixed caps via CSS variables from ItemGrid. */
.c-item-grid--density .c-file-browser__item {
  img, svg {
    max-width: 100% !important;
    max-height: var(--c-item-thumb-max, 110px) !important;
    width: auto !important;
    height: auto !important;
  }

  > svg {
    width: auto !important;
    height: auto !important;
    max-width: 100% !important;
    max-height: var(--c-item-thumb-max, 110px) !important;
  }
}

.c-item-grid--density:not(.folder-items) .c-file-browser__item {
  img {
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain;
  }
}

.c-item-grid--density.folder-items .content {
  height: var(--c-item-folder-preview-height, 88px) !important;

  img {
    max-height: var(--c-item-thumb-max, 80px) !important;
  }

  .folder-icon-preview {
    width: var(--c-item-folder-icon, 88px) !important;
    height: var(--c-item-folder-icon-h, 72px) !important;
    max-width: var(--c-item-folder-icon, 88px) !important;
    max-height: var(--c-item-folder-icon-h, 72px) !important;
  }
}
</style>
